import type {
	AiProviderKindPayload,
	AiProviderPayload,
} from '@Application/Ai/AiConfiguration/Providers/Actions/aiProvidersFetched';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `apiKeyRegisteredAt` is converted to a `Date`; `lastTestedAt` stays a `string | null` — the two
 * fields are treated differently on purpose, mirroring `IdentityProviderView`. `apiKey` has no place
 * here: the server never serves it back, on any route.
 * `lastTestModels` normalizes the payload's `null` (never tested) into an empty array: every
 * consumer gets a plain list to iterate, and `hasBeenTested` remains the only way to tell "never
 * tested" from "tested, nothing reported".
 */
class AiProviderView {
	readonly id: number;
	readonly label: string;
	readonly kind: AiProviderKindPayload;
	readonly baseUrl: string;
	readonly apiVersion: string | null;
	readonly apiKeyRegisteredAt: Date;
	readonly isPlatformCredential: boolean;
	readonly lastTestedAt: string | null;
	readonly lastTestSucceeded: boolean | null;
	readonly lastTestMessage: string | null;
	readonly lastTestModels: readonly string[];
	readonly accountId: number;

	constructor(payload: AiProviderPayload) {
		this.id = payload.id;
		this.label = payload.label;
		this.kind = payload.kind;
		this.baseUrl = payload.baseUrl;
		this.apiVersion = payload.apiVersion;
		this.apiKeyRegisteredAt = new Date(payload.apiKeyRegisteredAt);
		this.isPlatformCredential = payload.isPlatformCredential;
		this.lastTestedAt = payload.lastTestedAt;
		this.lastTestSucceeded = payload.lastTestSucceeded;
		this.lastTestMessage = payload.lastTestMessage;
		this.lastTestModels = payload.lastTestModels ?? [];
		this.accountId = payload.account.id;

		Object.freeze(this.lastTestModels);
		Object.freeze(this);
	}

	get hasBeenTested(): boolean {
		return this.lastTestSucceeded !== null;
	}

	/** Applies a fresh connection test result, leaving every other field untouched. */
	withConnectionTestResult(
		succeeded: boolean,
		testedAt: string,
		message: string,
		models: string[],
	): AiProviderView {
		return new AiProviderView({
			id: this.id,
			label: this.label,
			kind: this.kind,
			baseUrl: this.baseUrl,
			apiVersion: this.apiVersion,
			apiKeyRegisteredAt: this.apiKeyRegisteredAt.toISOString(),
			isPlatformCredential: this.isPlatformCredential,
			lastTestedAt: testedAt,
			lastTestSucceeded: succeeded,
			lastTestMessage: message,
			lastTestModels: models,
			account: { id: this.accountId },
		});
	}
}

export default AiProviderView;
